import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { ApiError, api } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  Brain,
  CheckCircle2,
  Link2,
  Network,
  RefreshCw,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

interface RunMeta {
  pageCount?: number;
  brokenLinkCount?: number;
  orphanCount?: number;
  affected?: Array<{ slug: string; action: 'created' | 'updated' | 'deleted' }>;
  report?: string;
  error?: string | null;
  scope?: 'category';
  categorySlug?: string;
  categoryName?: string;
}

export function AdminWikiHealthCheck() {
  const queryClient = useQueryClient();
  const runsQuery = useQuery({
    queryKey: ['admin', 'wiki', 'health-runs'],
    queryFn: api.adminWikiHealthCheckRuns,
    refetchInterval: 5_000,
  });

  const runMutation = useMutation({
    mutationFn: api.adminWikiHealthCheckRun,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'health-runs'] });
    },
  });

  const statsQuery = useQuery({
    queryKey: ['admin', 'wiki', 'graph-stats'],
    queryFn: api.adminWikiGraphStats,
    staleTime: 10_000,
  });

  const regenerateMutation = useMutation({
    mutationFn: api.adminWikiRegenerateHubs,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'graph-stats'] });
      queryClient.invalidateQueries({ queryKey: ['wiki'] });
      alert(`허브 ${res.regenerated}개 재구성 완료: ${res.categories.join(', ')}`);
    },
    onError: (err: unknown) => {
      alert(err instanceof ApiError ? err.message : '재구성에 실패했습니다.');
    },
  });

  const reconcileMutation = useMutation({
    mutationFn: api.adminWikiReconcileLinks,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'graph-stats'] });
      queryClient.invalidateQueries({ queryKey: ['wiki'] });
      alert(
        `wiki_links 재구축: 삭제 ${res.removed}건 → 신규 ${res.inserted}건, 끊긴 링크 ${res.brokenLinks}건`,
      );
    },
    onError: (err: unknown) => {
      alert(err instanceof ApiError ? err.message : '재구축에 실패했습니다.');
    },
  });

  const reembedMutation = useMutation({
    mutationFn: api.adminWikiReembedAll,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'graph-stats'] });
      alert(`임베딩 재계산 ${res.queued}개 큐잉. 워커가 백그라운드 처리.`);
    },
    onError: (err: unknown) => {
      alert(err instanceof ApiError ? err.message : '임베딩 큐잉 실패');
    },
  });

  const backfillMissingMutation = useMutation({
    mutationFn: api.adminWikiBackfillMissingEmbeddings,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'graph-stats'] });
      alert(`임베딩 미존재 ${res.queued}개 backfill 큐잉.`);
    },
    onError: (err: unknown) => {
      alert(err instanceof ApiError ? err.message : 'backfill 큐잉 실패');
    },
  });

  const curateAllMutation = useMutation({
    mutationFn: api.adminWikiCurateAllHubs,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'graph-stats'] });
      queryClient.invalidateQueries({ queryKey: ['wiki'] });
      alert(res.message);
    },
    onError: (err: unknown) => {
      alert(err instanceof ApiError ? err.message : '큐레이션 큐잉에 실패했습니다.');
    },
  });

  // 일괄 재ingest (Track A 분석 기존 자료 적용)
  const reingestStatusQuery = useQuery({
    queryKey: ['admin', 'wiki', 'reingest-status'],
    queryFn: api.adminWikiReingestStatus,
    refetchInterval: 5_000,
  });
  const reingestStatus = reingestStatusQuery.data;
  const reingestRunning = reingestStatus?.status === 'running';
  const reingestMutation = useMutation({
    mutationFn: api.adminWikiReingestAll,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'reingest-status'] });
      alert(res.message);
    },
    onError: (err: unknown) => {
      alert(err instanceof ApiError ? err.message : '일괄 재ingest 큐잉 실패');
    },
  });

  const runs = runsQuery.data?.runs ?? [];
  const stats = statsQuery.data;

  return (
    <PageShell
      header={
        <PageHeader
          title="위키 건강검진"
          description="LLM 이 위키 전체를 점검해 모순/중복/끊긴 링크/고아 페이지를 정리합니다. 큰 위키는 비용이 큽니다 — 사용자가 명시적으로 호출해야 실행됩니다."
        />
      }
    >
      <div className="space-y-6">
        {/* 그래프 통계 카드 */}
        <section className="rounded-md border border-border bg-surface p-4">
          <header className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-semibold flex items-center gap-2">
              <Network className="h-4 w-4" /> 그래프 통계
            </h2>
            <button
              type="button"
              onClick={() => statsQuery.refetch()}
              disabled={statsQuery.isFetching}
              className="text-xs text-foreground-muted hover:text-foreground"
            >
              <RefreshCw
                className={`inline h-3 w-3 ${statsQuery.isFetching ? 'animate-spin' : ''}`}
              />{' '}
              새로고침
            </button>
          </header>
          {statsQuery.isLoading ? (
            <p className="text-sm text-foreground-muted">불러오는 중…</p>
          ) : !stats ? (
            <p className="text-sm text-destructive">통계를 불러오지 못했습니다.</p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <Stat
                  label="페이지"
                  value={`${stats.totalPages}`}
                  sub={`LLM ${stats.llmPages} · sys ${stats.systemPages}`}
                />
                <Stat
                  label="링크"
                  value={`${stats.totalLinks}`}
                  sub={`끊김 ${stats.brokenLinks}`}
                />
                <Stat
                  label="고립"
                  value={`${stats.isolatedPages}`}
                  sub={`leaf ${stats.leafOnlyPages}`}
                  tone={stats.isolatedPages > 0 ? 'warn' : 'ok'}
                />
                <Stat
                  label="평균 outlink"
                  value={stats.avgOutlinks.toFixed(2)}
                  sub={`backlink ${stats.avgBacklinks.toFixed(2)}`}
                  tone={stats.avgOutlinks < 1 ? 'warn' : 'ok'}
                />
              </div>
              <div className="border-t border-border pt-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-xs">
                    <Brain className="size-3.5 text-foreground-muted" />
                    <span className="font-medium">임베딩</span>
                    <span className="text-foreground-muted">
                      {stats.embeddings.embedded} / {stats.embeddings.total}
                      {stats.embeddings.pending > 0 ? ` (대기 ${stats.embeddings.pending})` : ' ✓'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {stats.embeddings.pending > 0 ? (
                      <button
                        type="button"
                        onClick={() => backfillMissingMutation.mutate()}
                        disabled={backfillMissingMutation.isPending}
                        className="inline-flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-700 dark:text-amber-300 hover:border-amber-500 disabled:opacity-50"
                        title="임베딩이 아직 없는 (또는 dead task 로 실패한) 페이지만 큐잉"
                      >
                        <Brain
                          className={`size-2.5 ${backfillMissingMutation.isPending ? 'animate-pulse' : ''}`}
                        />
                        {backfillMissingMutation.isPending
                          ? '큐잉 중…'
                          : `미존재 ${stats.embeddings.pending}개 backfill`}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          window.confirm(
                            `모든 살아있는 페이지 (${stats.embeddings.total}개) 의 임베딩을 재계산 큐에 등록합니다. embedding endpoint 호출 비용 발생. 계속?`,
                          )
                        ) {
                          reembedMutation.mutate();
                        }
                      }}
                      disabled={reembedMutation.isPending}
                      className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 text-[10px] hover:border-accent disabled:opacity-50"
                    >
                      <Brain
                        className={`size-2.5 ${reembedMutation.isPending ? 'animate-pulse' : ''}`}
                      />
                      {reembedMutation.isPending ? '큐잉 중…' : '전체 재 embed'}
                    </button>
                  </div>
                </div>
              </div>
              <div className="border-t border-border pt-3">
                <h3 className="mb-1 text-xs font-medium text-foreground-muted">
                  카테고리별 연결 밀도
                </h3>
                <ul className="space-y-1 text-xs">
                  {stats.categories.map((c) => (
                    <li key={c.slug} className="flex items-center justify-between">
                      <span className="font-mono">{c.slug}</span>
                      <span className="text-foreground-muted">
                        {c.pageCount}페이지 · 내부링크 {c.internalLinks} · 밀도{' '}
                        {(c.density * 100).toFixed(1)}%
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-wrap gap-2 border-t border-border pt-3">
                <button
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm(
                        '모든 카테고리의 hub 본문을 LLM 으로 큐레이션 (sub-cluster + annotation). 카테고리 수만큼 LLM 호출되어 비용/시간이 발생합니다. 계속?',
                      )
                    ) {
                      curateAllMutation.mutate();
                    }
                  }}
                  disabled={curateAllMutation.isPending}
                  className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-accent-foreground hover:opacity-90 disabled:opacity-50"
                >
                  <Sparkles
                    className={`h-3 w-3 ${curateAllMutation.isPending ? 'animate-spin' : ''}`}
                  />
                  {curateAllMutation.isPending ? 'LLM 큐레이션 중…' : 'LLM-curated MOC (전체)'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm(
                        '모든 카테고리 hub 본문을 단순 알파벳 dump 로 채웁니다 (LLM 사용 X). 큐레이션 fallback 용. 계속?',
                      )
                    ) {
                      regenerateMutation.mutate();
                    }
                  }}
                  disabled={regenerateMutation.isPending}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs hover:border-accent disabled:opacity-50"
                >
                  <RefreshCw
                    className={`h-3 w-3 ${regenerateMutation.isPending ? 'animate-spin' : ''}`}
                  />
                  {regenerateMutation.isPending ? '재구성 중…' : '단순 dump (fallback)'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm(
                        '모든 페이지 본문에서 [[]] 를 다시 추출해 wiki_links 를 재구축합니다. 데이터 손실 후 복구용. 계속?',
                      )
                    ) {
                      reconcileMutation.mutate();
                    }
                  }}
                  disabled={reconcileMutation.isPending}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs hover:border-accent disabled:opacity-50"
                >
                  <Link2
                    className={`h-3 w-3 ${reconcileMutation.isPending ? 'animate-spin' : ''}`}
                  />
                  {reconcileMutation.isPending ? '재구축 중…' : 'wiki_links 재구축'}
                </button>
              </div>
              <div className="border-t border-border pt-3">
                <h3 className="mb-2 text-xs font-medium text-foreground-muted">
                  카테고리별 큐레이션
                </h3>
                <ul className="space-y-1 text-xs">
                  {stats.categories
                    .filter((c) => c.pageCount > 0)
                    .map((c) => (
                      <li key={c.slug} className="flex items-center justify-between">
                        <span className="font-mono">
                          {c.slug} ({c.pageCount})
                        </span>
                        <CurateOneButton categorySlug={c.slug} />
                      </li>
                    ))}
                </ul>
              </div>
              <div className="border-t border-border pt-3">
                <h3 className="mb-1 text-xs font-medium text-foreground-muted">
                  카테고리별 건강검진
                </h3>
                <p className="mb-2 text-[10px] text-foreground-faint">
                  해당 카테고리 페이지만 LLM 으로 점검·정리합니다. 다른 카테고리는 읽기만 하고 변경
                  호출은 서버가 거부 — 전체 건강검진보다 안전하고 비용이 작습니다.
                </p>
                <ul className="space-y-1 text-xs">
                  {stats.categories
                    .filter((c) => c.pageCount > 0)
                    .map((c) => (
                      <li key={c.slug} className="flex items-center justify-between">
                        <span className="font-mono">
                          {c.slug} ({c.pageCount})
                        </span>
                        <HealthCheckOneButton categorySlug={c.slug} pageCount={c.pageCount} />
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          )}
        </section>

        {/* 기존 자료 카테고리 재분석 */}
        <section className="rounded-md border border-border bg-surface p-4">
          <header className="mb-2 flex items-center justify-between">
            <h2 className="text-base font-semibold flex items-center gap-2">
              <RotateCcw className="h-4 w-4" /> 기존 자료 카테고리 재분석
            </h2>
          </header>
          <p className="mb-3 text-xs text-foreground-muted">
            이미 넣어 둔 자료를 개선된 분석 로직 (의미가 비슷한 페이지 연결 + 카테고리 정리 보강)
            으로 다시 정리합니다. 한 번에 1개씩 순차 처리 — 서버 재시작 시 같은 버튼을 다시 눌러서
            미처리만 이어서 실행 가능 (이미 시도한 자료는 건너뜁니다).
          </p>
          {reingestStatus && reingestRunning ? (
            <div className="mb-3 rounded border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
              <div className="flex items-baseline justify-between">
                <span className="font-medium">
                  진행 중: {reingestStatus.processed} / {reingestStatus.total}
                  {reingestStatus.failed > 0 ? ` (실패 ${reingestStatus.failed})` : ''}
                </span>
                <span className="text-foreground-faint">
                  시작{' '}
                  {reingestStatus.startedAt
                    ? new Date(reingestStatus.startedAt).toLocaleString('ko-KR')
                    : '—'}
                </span>
              </div>
              {reingestStatus.lastStep ? (
                <div className="mt-1 text-foreground-muted">
                  마지막:{' '}
                  <code className="rounded bg-code-bg px-1">
                    {reingestStatus.lastStep.originalName ?? reingestStatus.lastStep.docId}
                  </code>{' '}
                  <span
                    className={
                      reingestStatus.lastStep.status === 'failed'
                        ? 'text-destructive'
                        : 'text-emerald-600'
                    }
                  >
                    ({reingestStatus.lastStep.status})
                  </span>
                  <span className="ml-2 text-foreground-faint">
                    {new Date(reingestStatus.lastStep.at).toLocaleTimeString('ko-KR')}
                  </span>
                </div>
              ) : null}
              {reingestStatus.lastStep?.error ? (
                <div className="mt-1 text-destructive">{reingestStatus.lastStep.error}</div>
              ) : null}
            </div>
          ) : reingestStatus && reingestStatus.status === 'done' ? (
            <div className="mb-3 rounded border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs">
              <span className="font-medium">
                마지막 재분석 완료: {reingestStatus.processed} / {reingestStatus.total}
                {reingestStatus.failed > 0 ? ` (실패 ${reingestStatus.failed})` : ''}
              </span>
              {reingestStatus.finishedAt ? (
                <span className="ml-2 text-foreground-faint">
                  {new Date(reingestStatus.finishedAt).toLocaleString('ko-KR')}
                </span>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => {
              if (
                window.confirm(
                  reingestRunning
                    ? '진행 중인 작업이 있습니다 — 아직 처리 안 된 자료만 다시 큐잉합니다. 계속?'
                    : '이미 넣어 둔 모든 자료를 다시 분석합니다. LLM 호출 비용 + 시간이 큽니다 (자료 수 × 청크 수). 계속?',
                )
              ) {
                reingestMutation.mutate();
              }
            }}
            disabled={reingestMutation.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-accent-foreground hover:opacity-90 disabled:opacity-50"
          >
            <RotateCcw className={`h-3 w-3 ${reingestMutation.isPending ? 'animate-spin' : ''}`} />
            {reingestMutation.isPending
              ? '큐잉 중…'
              : reingestRunning
                ? '미처리만 이어서 실행'
                : '기존 자료 재분석 시작'}
          </button>
        </section>

        <section>
          <button
            type="button"
            onClick={() => {
              if (
                window.confirm(
                  '건강검진을 실행합니다. 위키 전체 인덱스를 LLM 에 전달하므로 토큰 비용이 발생할 수 있습니다. 계속?',
                )
              ) {
                runMutation.mutate();
              }
            }}
            disabled={runMutation.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-foreground hover:opacity-90 disabled:opacity-50"
          >
            <Activity className="h-4 w-4" />
            {runMutation.isPending ? '큐에 등록 중…' : '건강검진 실행'}
          </button>
          {runMutation.isSuccess ? (
            <span className="ml-3 text-sm text-emerald-600">
              워커에 등록되었습니다. 결과가 아래에 표시됩니다.
            </span>
          ) : null}
        </section>

        <section>
          <h2 className="mb-3 text-lg font-semibold">최근 실행 (최대 20건)</h2>
          {runsQuery.isLoading ? (
            <p className="text-sm text-foreground-muted">로딩 중…</p>
          ) : runs.length === 0 ? (
            <div className="rounded-md border border-border bg-surface p-6 text-center text-foreground-muted">
              아직 실행된 건강검진이 없습니다.
            </div>
          ) : (
            <ul className="space-y-3">
              {runs.map((r) => {
                const meta = r.meta as RunMeta;
                return (
                  <li key={r.id} className="rounded-md border border-border bg-surface p-4">
                    <div className="flex items-baseline justify-between">
                      <div className="flex items-center gap-2">
                        {r.status === 'failed' ? (
                          <AlertTriangle className="h-4 w-4 text-destructive" />
                        ) : (
                          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        )}
                        <span className="font-medium">
                          {r.status === 'failed' ? '실패' : '완료'}
                        </span>
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] ${
                            meta.scope === 'category'
                              ? 'bg-accent/10 text-accent'
                              : 'bg-foreground/5 text-foreground-muted'
                          }`}
                        >
                          {meta.scope === 'category'
                            ? `카테고리: ${meta.categoryName ?? meta.categorySlug}`
                            : '전체'}
                        </span>
                      </div>
                      <span className="text-xs text-foreground-faint">
                        {new Date(r.createdAt).toLocaleString('ko-KR')}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-foreground-muted">
                      <span>페이지 {meta.pageCount ?? 0}개</span>
                      <span>끊긴 링크 {meta.brokenLinkCount ?? 0}건</span>
                      <span>고아 {meta.orphanCount ?? 0}개</span>
                    </div>
                    {meta.affected && meta.affected.length > 0 ? (
                      <div className="mt-2 text-xs">
                        <span className="text-foreground-muted">변경된 페이지:</span>{' '}
                        {meta.affected.map((a, i) => (
                          <span key={a.slug}>
                            {i > 0 ? ', ' : ''}
                            <code className="rounded bg-code-bg px-1">{a.slug}</code>
                            <span
                              className={`ml-1 ${
                                a.action === 'deleted'
                                  ? 'text-destructive'
                                  : a.action === 'created'
                                    ? 'text-emerald-600'
                                    : 'text-amber-600'
                              }`}
                            >
                              ({a.action})
                            </span>
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {meta.error ? (
                      <div className="mt-2 rounded bg-destructive/10 px-3 py-2 text-xs text-destructive">
                        {meta.error}
                      </div>
                    ) : null}
                    {meta.report ? (
                      <details className="mt-3">
                        <summary className="cursor-pointer text-xs text-accent">
                          LLM 보고서 보기
                        </summary>
                        <pre className="mt-2 whitespace-pre-wrap rounded bg-code-bg p-3 text-xs leading-6">
                          {meta.report}
                        </pre>
                      </details>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </PageShell>
  );
}

function HealthCheckOneButton({
  categorySlug,
  pageCount,
}: {
  categorySlug: string;
  pageCount: number;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api.adminWikiHealthCheckRunCategory(categorySlug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'health-runs'] });
      toast.success(`'${categorySlug}' 카테고리 건강검진을 시작했습니다`);
    },
    onError: (err: unknown) => {
      toast.error(err instanceof ApiError ? err.message : '건강검진 큐잉 실패');
    },
  });
  return (
    <button
      type="button"
      onClick={() => {
        if (
          window.confirm(
            `'${categorySlug}' 카테고리(${pageCount}페이지) 만 LLM 으로 건강검진합니다. 토큰 비용이 발생합니다. 계속?`,
          )
        ) {
          mutation.mutate();
        }
      }}
      disabled={mutation.isPending}
      className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 text-[10px] hover:border-accent disabled:opacity-50"
    >
      <Activity className={`h-2.5 w-2.5 ${mutation.isPending ? 'animate-spin' : ''}`} />
      {mutation.isPending ? '…' : '건강검진'}
    </button>
  );
}

function CurateOneButton({ categorySlug }: { categorySlug: string }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api.adminWikiCurateHub(categorySlug),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'graph-stats'] });
      queryClient.invalidateQueries({ queryKey: ['wiki'] });
      alert(res.message);
    },
    onError: (err: unknown) => {
      alert(err instanceof ApiError ? err.message : '큐레이션 큐잉 실패');
    },
  });
  return (
    <button
      type="button"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 text-[10px] hover:border-accent disabled:opacity-50"
    >
      <Sparkles className={`h-2.5 w-2.5 ${mutation.isPending ? 'animate-spin' : ''}`} />
      {mutation.isPending ? '…' : 'MOC 큐레이션'}
    </button>
  );
}

function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'ok' | 'warn';
}) {
  const valueColor =
    tone === 'warn' ? 'text-amber-600' : tone === 'ok' ? 'text-emerald-600' : 'text-foreground';
  return (
    <div className="rounded border border-border bg-background px-3 py-2">
      <div className="text-xs text-foreground-muted">{label}</div>
      <div className={`text-lg font-semibold ${valueColor}`}>{value}</div>
      {sub ? <div className="text-[10px] text-foreground-faint mt-0.5">{sub}</div> : null}
    </div>
  );
}
